import type { ProposedSlot } from "@/types/scheduling.ts";
import { cn } from "@/lib/utils.ts";

/** Format an epoch-ms slot as "Tue, Jun 3 · 2:00 – 2:30 PM" for card display. */
export function formatProposedSlot(slot: ProposedSlot): string {
  const start = new Date(slot.startMsSinceEpoch);
  const end = new Date(slot.endMsSinceEpoch);
  const day = start.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const time = (d: Date) => d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${day} · ${time(start)} – ${time(end)}`;
}

const CARD_BASE_CLASS = "rounded-md border bg-muted/40 px-3 py-2 my-2";

/**
 * Read-only / clickable card listing the kickoff's proposed times.
 *
 * Used by `KickoffBodyWithSlotsBlock` (the schema-driven view, embedded
 * between body halves). The consumer manages its own picker open/close state
 * and passes `onEdit` (or omits it for the post-send read-only view). Without
 * `onEdit` the card renders as a plain div; with it, as a button that
 * hover-highlights — the editable pattern that exposes "click to edit slots"
 * without showing an extra pencil icon.
 */
interface ProposedTimesCardProps {
  slots: ReadonlyArray<ProposedSlot>;
  onEdit?: () => void;
  statsigName?: string;
}

export const ProposedTimesCard = ({ slots, onEdit, statsigName }: ProposedTimesCardProps) => {
  const sorted = [...slots].sort((a, b) => a.startMsSinceEpoch - b.startMsSinceEpoch);
  const list = (
    <div className="flex flex-col gap-1">
      {sorted.length === 0 ? (
        <span className="text-sm text-muted-foreground">No times proposed</span>
      ) : (
        sorted.map((s) => (
          <span key={`${s.startMsSinceEpoch}-${s.endMsSinceEpoch}`} className="text-sm">
            {formatProposedSlot(s)}
          </span>
        ))
      )}
    </div>
  );

  if (onEdit) {
    return (
      <button
        type="button"
        onClick={onEdit}
        aria-label="Edit proposed times"
        data-statsig-name={statsigName}
        className={cn(
          CARD_BASE_CLASS,
          "w-full text-left hover:border-primary hover:bg-muted/60 cursor-pointer",
        )}
      >
        <div className="mb-1 text-xs font-medium text-muted-foreground">Proposed times</div>
        {list}
      </button>
    );
  }

  return (
    <div className={CARD_BASE_CLASS}>
      <div className="mb-1 text-xs font-medium text-muted-foreground">Proposed times</div>
      {list}
    </div>
  );
};
